<template>
  <main class="descriptions">
    <div class="descriptions__header">
      <game-selector :selectedGame="selectedGame" @select="onGameSelect" />

      <div class="descriptions__actions">
        <button
          class="descriptions__btn"
          :class="{ active: currentView === 'list' }"
          @click="setView('list')"
        >
          Список описаний
        </button>
        <button
          class="descriptions__btn"
          :class="{ active: currentView === 'create' }"
          @click="setView('create')"
        >
          Создать описание
        </button>
      </div>
    </div>

    <div class="descriptions__content" v-if="currentView === 'list'">
      <div class="descriptions__search">
        <div class="descriptions__searchInput">
          <img src="../../assets/images/search.svg" alt="search" />
          <input
            type="text"
            v-model="searchName"
            placeholder="Поиск по названию..."
            @input="onSearchInput"
          />
        </div>
      </div>

      <div class="descriptions__loader" v-if="loading">
        <Loader />
      </div>

      <div class="descriptions__table" v-if="!loading">
        <table>
          <thead>
            <tr>
              <td class="id">ID</td>
              <td class="name">Name</td>
              <td class="values">Values</td>
              <td class="actions">Действия</td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="description in descriptions" :key="description.id">
              <td class="id">{{ description.id }}</td>
              <td class="name">{{ description.name }}</td>
              <td class="values">
                <div class="values__preview" @click="toggleValues(description.id)">
                  <span>{{ getValuesPreview(description.values) }}</span>
                  <img
                    :class="{ rotate: expandedValues.includes(description.id) }"
                    src="../../assets/images/down-arrow.svg"
                    alt="expand"
                  />
                </div>
                <div class="values__expanded" v-if="expandedValues.includes(description.id)">
                  <div v-for="(value, lang) in description.values" :key="lang" class="values__item">
                    <span class="values__lang">{{ lang }}:</span>
                    <span class="values__text">{{ value }}</span>
                  </div>
                </div>
              </td>
              <td class="actions">
                <button class="descriptions__editBtn" @click="editDescription(description)">
                  <img src="../../assets/images/pen.svg" alt="edit" />
                </button>
                <button class="descriptions__deleteBtn" @click="deleteDescription(description.id)">
                  <img src="../../assets/images/delete.svg" alt="delete" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="descriptions.length === 0" class="descriptions__empty">Нет описаний</p>
      </div>

      <div class="descriptions__pagination" v-if="!loading && pagination.total > 0">
        <div class="descriptions__paginationInfo">
          Показано {{ pagination.skip + 1 }}-{{
            Math.min(pagination.skip + pagination.limit, pagination.total)
          }}
          из {{ pagination.total }}
        </div>
        <div class="descriptions__paginationControls">
          <button
            class="descriptions__paginationBtn"
            :disabled="pagination.skip === 0"
            @click="prevPage"
          >
            <img src="../../assets/images/arrowLeft.svg" alt="prev" />
            Назад
          </button>

          <div class="descriptions__paginationPages">
            <button
              v-for="page in getVisiblePages()"
              :key="page"
              class="descriptions__paginationPage"
              :class="{ active: page === getCurrentPage() }"
              @click="goToPage(page - 1)"
            >
              {{ page }}
            </button>
          </div>

          <button
            class="descriptions__paginationBtn"
            :disabled="pagination.skip + pagination.limit >= pagination.total"
            @click="nextPage"
          >
            Вперед
            <img src="../../assets/images/right-arrow.svg" alt="next" />
          </button>
        </div>
      </div>
    </div>

    <div class="descriptions__form" v-if="currentView === 'create'">
      <div class="descriptions__formHeader">
        <h2>{{ isEditing ? 'Редактировать описание' : 'Создать описание' }}</h2>
        <button class="descriptions__backBtn" @click="setView('list')">
          <img src="../../assets/images/arrowLeft.svg" alt="back" />
          <span>Вернуться к списку</span>
        </button>
      </div>

      <form @submit.prevent="saveDescription" class="descriptions__formContent">
        <div class="descriptions__formGroup">
          <label>Название:</label>
          <input
            type="text"
            v-model="formData.name"
            placeholder="Введите название описания"
            required
          />
        </div>

        <div class="descriptions__formGroup">
          <label>Языки и тексты:</label>
          <div class="descriptions__languages">
            <div
              v-for="(lang, index) in formData.languages"
              :key="index"
              class="descriptions__languageItem"
            >
              <div class="descriptions__languageHeader">
                <select v-model="lang.code" class="descriptions__langCode">
                  <option value="" disabled>Код языка</option>
                  <option v-for="code in getLangOptions(index)" :key="code" :value="code">
                    {{ code }}
                  </option>
                </select>
                <button
                  type="button"
                  class="descriptions__removeLangBtn"
                  @click="removeLanguage(index)"
                  v-if="formData.languages.length > 1"
                >
                  <img src="../../assets/images/close.svg" alt="remove" />
                </button>
              </div>
              <textarea
                v-model="lang.text"
                placeholder="Текст описания на этом языке"
                class="descriptions__langText"
                required
              ></textarea>
            </div>
          </div>
          <button type="button" class="descriptions__addLangBtn" @click="addLanguage">
            <img src="../../assets/images/plus.svg" alt="add" />
            Добавить язык
          </button>
        </div>

        <div class="descriptions__formActions">
          <button type="submit" class="descriptions__saveBtn">
            {{ isEditing ? 'Сохранить изменения' : 'Создать описание' }}
          </button>
          <button type="button" class="descriptions__cancelBtn" @click="cancelEdit">Отмена</button>
        </div>
      </form>
    </div>
  </main>
</template>

<script>
import Loader from '@/components/Loader/Loader.vue'
import GameSelector from '@/components/ui/GameSelector/GameSelector.vue'
import DescriptionsApi from '@/utils/descriptionsApi'
import { Games, Langs } from '@/constants'
import './descriptions.scss'

export default {
  name: 'Descriptions',
  components: {
    Loader,
    GameSelector
  },
  data() {
    return {
      selectedGame: Games.ADOPTME,
      currentView: 'list',
      loading: false,
      descriptions: [],
      expandedValues: [],
      isEditing: false,
      editingId: null,
      formData: {
        name: '',
        languages: [
          { code: 'en', text: '' },
          { code: 'ru', text: '' }
        ]
      },
      pagination: {
        skip: 0,
        limit: 10,
        total: 0,
        pages: 0
      },
      searchName: '',
      searchTimeout: null,
      api: null
    }
  },
  computed: {
    isPermissions() {
      return this.$store.state.permissions
    },
    baseApiUrl() {
      return 'https://mg.apineural.com/api'
    }
  },
  mounted() {
    this.updateApiInstance()
    this.loadDescriptions()
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    updateApiInstance() {
      this.api = new DescriptionsApi(this.baseApiUrl, this.$store.state.tokens.access)
    },
    onGameSelect({ game }) {
      this.selectedGame = game
      this.pagination.skip = 0
      this.searchName = ''
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }
      this.loadDescriptions()
    },
    setView(view) {
      this.currentView = view
      if (view === 'list') {
        this.resetForm()
      }
    },
    async loadDescriptions() {
      this.loading = true
      try {
        const data = await this.api.getDescriptionsList({
          page: this.getCurrentPage(),
          limit: this.pagination.limit,
          name: this.searchName,
          game: this.selectedGame
        })

        this.descriptions = (data.items || []).map((desc) => ({
          id: desc.id,
          name: desc.name,
          values: desc.values
        }))

        this.pagination.total = data.total || 0
        this.pagination.pages = data.pages || 0
      } catch (error) {
        console.error('Error loading descriptions:', error)
        this.descriptions = []
        this.pagination.total = 0
        this.pagination.pages = 0
      } finally {
        this.loading = false
      }
    },
    nextPage() {
      if (this.pagination.skip + this.pagination.limit < this.pagination.total) {
        this.pagination.skip += this.pagination.limit
        this.loadDescriptions()
      }
    },
    prevPage() {
      if (this.pagination.skip > 0) {
        this.pagination.skip = Math.max(0, this.pagination.skip - this.pagination.limit)
        this.loadDescriptions()
      }
    },
    goToPage(page) {
      this.pagination.skip = page * this.pagination.limit
      this.loadDescriptions()
    },
    toggleValues(id) {
      const index = this.expandedValues.indexOf(id)
      if (index > -1) {
        this.expandedValues.splice(index, 1)
      } else {
        this.expandedValues.push(id)
      }
    },
    getValuesPreview(values) {
      const languages = Object.keys(values || {})
      if (languages.length === 0) return 'Нет значений'
      if (languages.length === 1)
        return `${languages[0]}: ${values[languages[0]].substring(0, 30)}...`
      return `${languages.length} языков (${languages.join(', ')})`
    },
    editDescription(description) {
      const values = description.values || {}

      this.isEditing = true
      this.editingId = description.id
      this.formData.name = description.name
      // values на бэке заменяется целиком, поэтому в форму тянем все языки описания —
      // иначе сохранение сотрёт те, что не попали в форму
      this.formData.languages = Langs.filter((code) => values[code] !== undefined).map((code) => ({
        code,
        text: values[code]
      }))
      this.setView('create')
    },
    async deleteDescription(id) {
      if (!confirm('Вы уверены, что хотите удалить это описание?')) return

      try {
        await this.api.deleteDescriptionById(id)
        this.loadDescriptions()
      } catch (error) {
        console.error('Error deleting description:', error)
        alert('Ошибка при удалении описания')
      }
    },
    getLangOptions(index) {
      const usedCodes = this.formData.languages
        .filter((lang, i) => i !== index)
        .map((lang) => lang.code)

      return Langs.filter((code) => !usedCodes.includes(code))
    },
    addLanguage() {
      const nextCode = this.getLangOptions(-1)[0] || ''
      this.formData.languages.push({ code: nextCode, text: '' })
    },
    removeLanguage(index) {
      this.formData.languages.splice(index, 1)
    },
    async saveDescription() {
      try {
        const values = {}
        this.formData.languages.forEach((lang) => {
          if (lang.code && lang.text) {
            values[lang.code] = lang.text
          }
        })

        if (this.isEditing) {
          await this.api.updateDescription(this.editingId, {
            name: this.formData.name,
            values
          })
        } else {
          await this.api.createDescription({
            game: this.selectedGame,
            name: this.formData.name,
            values
          })
        }

        this.resetForm()
        this.setView('list')
        this.loadDescriptions()
      } catch (error) {
        console.error('Error saving description:', error)
        alert('Ошибка при сохранении описания')
      }
    },
    cancelEdit() {
      this.resetForm()
      this.setView('list')
    },
    resetForm() {
      this.isEditing = false
      this.editingId = null
      this.formData = {
        name: '',
        languages: [
          { code: 'en', text: '' },
          { code: 'ru', text: '' }
        ]
      }
    },
    getVisiblePages() {
      const totalPages =
        this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.limit)
      const currentPage = this.getCurrentPage()
      const maxVisible = 5

      if (totalPages <= maxVisible) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
      }

      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },
    getCurrentPage() {
      return Math.floor(this.pagination.skip / this.pagination.limit) + 1
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.pagination.skip = 0
        this.loadDescriptions()
      }, 500)
    }
  }
}
</script>
